import { unwrap } from "@sagot/api-client";
import { API_KEY_ROTATION_OVERLAP_HOURS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Dialog, DialogContent, Icon } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import type { ApiKeyOut } from "../../shared";
import { SecretOnce } from "./panel-kit";

const K = "settings.api";
const MINUTE_MS = 60_000;
const TICK_MS = 15_000;

/** Whole hours and minutes left (never negative). */
export function remainingParts(ms: number): { hours: number; minutes: number } {
  const total = Math.max(0, Math.floor(ms / MINUTE_MS));
  return { hours: Math.floor(total / 60), minutes: total % 60 };
}

/** Milliseconds until `until`, re-read every 15 s so a countdown on screen stays honest. */
export function useRemainingMs(until: string): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => { setNow(Date.now()); }, TICK_MS);
    return () => { window.clearInterval(timer); };
  }, []);
  return new Date(until).getTime() - now;
}

/** "23 h 59 min", "42 min" or "under a minute" for the time left until `until`. */
function useRemainingText(until: string): string | null {
  const { t } = useTranslation();
  const ms = useRemainingMs(until);
  if (ms <= 0) return null;
  const { hours, minutes } = remainingParts(ms);
  if (hours > 0) return t(`${K}.remaining.hm`, { hours, minutes });
  if (minutes > 0) return t(`${K}.remaining.m`, { minutes });
  return t(`${K}.remaining.soon`);
}

/** "Old secret retires in 18 h 20 min": the previous secret of a rotated key is still accepted until then. */
export function RetireBadge({ until, label }: { until: string; label?: (time: string) => string }) {
  const { t } = useTranslation();
  const time = useRemainingText(until);
  if (time === null) return null;
  return (
    <Badge tone="warning" data-testid="retire-badge">
      <Icon name="timer" className="size-3" />
      {label ? label(time) : t(`${K}.retires`, { time })}
    </Badge>
  );
}

function Countdown({ until }: { until: string }) {
  const { t } = useTranslation();
  const time = useRemainingText(until);
  return (
    <p className="flex flex-wrap items-center gap-2 text-sm" data-testid="rotate-countdown">
      <Icon name="timer" className="size-4 text-fg-muted" />
      <span className="text-fg-muted">{t(`${K}.rotateDialog.countdown`)}</span>
      {/* role=timer announces nothing on its own, so a tick never interrupts a screen reader */}
      <strong role="timer" aria-live="off" className="font-semibold tabular-nums">
        {time ?? t(`${K}.remaining.over`)}
      </strong>
    </p>
  );
}

interface RotateKeyDialogProps {
  apiKey: ApiKeyOut;
  onClose: () => void;
  onRotated: () => Promise<unknown>;
}

/** Two steps: confirm (what happens to the old secret) -> the new key shown once with the overlap countdown. */
export function RotateKeyDialog({ apiKey, onClose, onRotated }: RotateKeyDialogProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const [result, setResult] = useState<{ secret: string; until: string } | null>(null);
  const rotate = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/api-keys/{key_id}/rotate", { params: { path: { key_id: apiKey.id } } })),
    onSuccess: async (data) => {
      setResult({ secret: data.secret, until: data.previous_valid_until });
      await onRotated();
    },
  });
  const close = (open: boolean) => {
    if (!open) onClose();
  };

  if (result) {
    return (
      <Dialog open onOpenChange={close}>
        <DialogContent title={t(`${K}.rotateDialog.doneTitle`)} closeLabel={t("common.close")} footer={<Button onClick={onClose}>{t(`${K}.rotateDialog.saved`)}</Button>}>
          <div className="space-y-4">
            <SecretOnce value={result.secret} label={t(`${K}.rotateDialog.secretLabel`)} warning={<><strong>{t(`${K}.rotateDialog.warningStrong`)}</strong> {t(`${K}.rotateDialog.warning`)}</>} />
            <Countdown until={result.until} />
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Dialog open onOpenChange={close}>
      <DialogContent
        title={t(`${K}.rotateDialog.title`, { name: apiKey.name })}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button loading={rotate.isPending} onClick={() => { rotate.mutate(); }}>
              <Icon name="refresh-cw" />
              {t(`${K}.rotateDialog.confirm`)}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <p>{t(`${K}.rotateDialog.intro`, { hours: API_KEY_ROTATION_OVERLAP_HOURS })}</p>
          {apiKey.rotation ? (
            <div className="flex items-start gap-2 rounded-md border border-line bg-warning-soft p-3 text-xs text-fg" role="note">
              <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0 text-warning" />
              <span>{t(`${K}.rotateDialog.again`)}</span>
            </div>
          ) : null}
          {/* Stays in the dialog until the next try (a toast is gone before a slow reader sees it): a 429 reads "Too many attempts. Wait a moment and try again." (BUG-M29-EV-B-011) */}
          {rotate.isError ? (
            <p role="alert" data-testid="rotate-error" className="rounded-md border border-line bg-danger-soft p-3 text-xs font-semibold text-danger">
              {message(rotate.error)}
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
